import React, { useEffect, useMemo, useState } from "react";
import { createReferral, fetchContactsAll, fetchMyReferrals } from "../Api";

const CITY_FORM_LINKS = {
  // Taksi İzmir için paylaştığın resmi kayıt formu
  taksi_izmir:
    "https://forms.fleet.yandex.com/forms?specification=taxi&skip_vehicle=1&ref_id=c76c3c39856d4115b6c48bc2a87ddf94",
};

function statusPill(status) {
  const waiting = (status || "").toLowerCase().includes("beklen");
  return {
    label: status || "-",
    style: {
      display: "inline-block",
      marginTop: 6,
      borderRadius: 999,
      padding: "3px 10px",
      fontSize: 12,
      fontWeight: 800,
      background: waiting ? "#facc15" : "#d1fae5",
      color: waiting ? "#78350f" : "#065f46",
    },
  };
}

function formatTrPhoneInput(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  let local = digits;

  // +90 ile başlayan ya da 0 ile başlayan girdileri normalize et
  if (local.startsWith("90")) local = local.slice(2);
  if (local.startsWith("0")) local = local.slice(1);

  // TR mobil formatı: 5XX XXX XXXX => toplam 10 hane
  local = local.slice(0, 10);

  let out = "+90";
  if (local.length > 0) out += ` ${local.slice(0, 3)}`;
  if (local.length > 3) out += ` ${local.slice(3, 6)}`;
  if (local.length > 6) out += ` ${local.slice(6, 10)}`;
  return out;
}

export default function ReferralPage() {
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("+90");
  const [cityContacts, setCityContacts] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchContactsAll();
        setCityContacts(Array.isArray(data?.items) ? data.items : []);
      } catch {
        setCityContacts([]);
      }
    })();
    (async () => {
      try {
        const data = await fetchMyReferrals();
        setRows(Array.isArray(data?.items) ? data.items : []);
      } catch {
        setRows([]);
      }
    })();
  }, []);

  const localDigits = useMemo(() => {
    const digits = String(phone || "").replace(/\D/g, "");
    let local = digits;
    if (local.startsWith("90")) local = local.slice(2);
    if (local.startsWith("0")) local = local.slice(1);
    return local.slice(0, 10);
  }, [phone]);

  const isPhoneComplete = localDigits.length === 10 && localDigits.startsWith("5");

  const selectedCity = useMemo(
    () => cityContacts.find((x) => (x?.park_key || "") === city) || null,
    [cityContacts, city]
  );
  const manager = selectedCity?.contacts?.[0] || null;

  const inviterName = useMemo(() => {
    try {
      const p = JSON.parse(localStorage.getItem("profile") || "{}");
      const first = (p?.first_name || "").trim();
      const last = (p?.last_name || "").trim();
      return [first, last].filter(Boolean).join(" ") || "Arkadaşın";
    } catch {
      return "Arkadaşın";
    }
  }, []);

  const inviteMessage = useMemo(() => {
    const managerName = manager?.name || "Yönetici";
    const managerPhone = manager?.phone || "-";
    const formLink = CITY_FORM_LINKS[city] || "";
    return `Merhaba, arkadaşın ${inviterName} seni Yandex Go taksi şoförü olarak kayıt olmaya davet ediyor.
${selectedCity?.city_label || "Seçilen şehir"} için kayıt işlemleriyle ${managerName} (${managerPhone}) ilgileniyor.
Bu numarayla hemen iletişime geç, kaydını tamamla; hem sen hem arkadaşın bonus kazanın.${formLink ? `\nKayıt bağlantısı: ${formLink}` : ""}`;
  }, [inviterName, manager, selectedCity, city]);

  const inviteeDigits = useMemo(() => {
    if (!isPhoneComplete) return "";
    // WhatsApp hedefi davet edilen kişi olmalı.
    return `90${localDigits}`;
  }, [isPhoneComplete, localDigits]);

  const waUrl = useMemo(() => {
    if (!inviteeDigits) return "#";
    return `https://wa.me/${inviteeDigits}?text=${encodeURIComponent(inviteMessage)}`;
  }, [inviteeDigits, inviteMessage]);

  const tgUrl = useMemo(
    () => `https://t.me/share/url?url=&text=${encodeURIComponent(inviteMessage)}`,
    [inviteMessage]
  );

  const viberUrl = useMemo(
    () => `viber://forward?text=${encodeURIComponent(inviteMessage)}`,
    [inviteMessage]
  );

  const directFormLink = useMemo(() => CITY_FORM_LINKS[city] || "", [city]);

  function mapStatusLabel(s) {
    const st = String(s || "").toLowerCase();
    if (st === "reward_paid") return "Hak verildi";
    if (st === "reward_pending") return "Hak bekliyor";
    if (st === "registered") return "Kayıt onaylandı";
    if (st === "active") return "Aktif";
    if (st === "rejected") return "Reddedildi";
    return "Kayıt bekleniyor";
  }

  async function ensureReferral() {
    if (!city || !isPhoneComplete || busy) return null;
    setBusy(true);
    try {
      const res = await createReferral({
        park_key: city,
        invitee_phone: phone,
        inviter_name: inviterName,
      });
      const created = res?.item;
      if (created) {
        setRows((prev) => {
          const arr = Array.isArray(prev) ? prev.slice() : [];
          const idx = arr.findIndex((x) => x.id === created.id);
          if (idx >= 0) arr[idx] = created;
          else arr.unshift(created);
          return arr;
        });
      }
      return created || null;
    } catch {
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function shareVia(url) {
    const created = await ensureReferral();
    // API dönmese bile kullanıcı girdisini tabloda anlık göster
    if (!created) {
      setRows((prev) => {
        const arr = Array.isArray(prev) ? prev.slice() : [];
        const normalizedInputPhone = formatTrPhoneInput(phone);
        const exists = arr.some((x) => String(x?.invitee_phone || x?.phone || "") === normalizedInputPhone);
        if (!exists) {
          arr.unshift({
            id: `tmp_${Date.now()}`,
            invitee_phone: normalizedInputPhone,
            status: "invited",
            orders_count: 0,
            reward_amount: 0,
            created_at: new Date().toISOString(),
          });
        }
        return arr;
      });
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function copyInvite() {
    await ensureReferral();
    try {
      // Kullanıcı "bağlantıyı kopyala" dediğinde link kopyalansın.
      const textToCopy = directFormLink || inviteMessage;
      await navigator.clipboard.writeText(textToCopy);
      window.alert(directFormLink ? "Bağlantı kopyalandı." : "Davet metni kopyalandı.");
    } catch {
      window.alert("Kopyalanamadı.");
    }
  }

  return (
    <div className="driver-page-wrap">
      <h2 className="driver-page-title" style={{ fontSize: 34 }}>
        Arkadaşını Getir - Bonus Kazan
      </h2>
      <div className="driver-page-subtitle">
        Bir şoför davet edin ve onun yolculukları için bonus kazanın
      </div>

      <div style={{ marginTop: 18, color: "#111827", fontWeight: 800, fontSize: 14 }}>
        Arkadaşının çalışacağı şehri seçin
      </div>
      <select
        value={city}
        onChange={(e) => setCity(e.target.value)}
        style={{
          marginTop: 8,
          width: "100%",
          height: 46,
          borderRadius: 8,
          border: "1px solid #d1d5db",
          background: "#fff",
          padding: "0 12px",
          fontWeight: 600,
          color: "#111827",
        }}
      >
        <option value="">Şehir seçin</option>
        {cityContacts.map((c) => (
          <option key={c.park_key} value={c.park_key}>
            {c.city_label}
          </option>
        ))}
      </select>

      {city ? (
        <>
          <div style={{ marginTop: 16, color: "#111827", fontWeight: 800, fontSize: 14 }}>
            Arkadaşınızın YandexPro kaydında kullanacağı telefon numarası
          </div>
          <input
            value={phone}
            onChange={(e) => setPhone(formatTrPhoneInput(e.target.value))}
            placeholder="+90"
            inputMode="numeric"
            maxLength={16}
            style={{
              marginTop: 8,
              width: "100%",
              height: 46,
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: "#fff",
              padding: "0 12px",
              fontWeight: 600,
              color: "#111827",
            }}
          />
        </>
      ) : null}

      {city && isPhoneComplete ? (
        <div style={{ marginTop: 18 }}>
          <div style={{ color: "#111827", fontWeight: 800, fontSize: 14 }}>Davet gönderin</div>
          <div className="driver-share-buttons">
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.preventDefault();
                shareVia(waUrl);
              }}
              style={inviteBtn("#ecfeff", "#0891b2")}
            >
              WhatsApp
            </a>
            <a
              href={tgUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.preventDefault();
                shareVia(tgUrl);
              }}
              style={inviteBtn("#eef2ff", "#4f46e5")}
            >
              Telegram
            </a>
            <a
              href={viberUrl}
              onClick={(e) => {
                e.preventDefault();
                shareVia(viberUrl);
              }}
              style={inviteBtn("#f5f3ff", "#7c3aed")}
            >
              Viber
            </a>
            <button type="button" onClick={copyInvite} style={inviteBtn("#f8fafc", "#334155")}>
              Bağlantıyı kopyala
            </button>
          </div>
          <div style={{ marginTop: 6, color: "#6b7280", fontSize: 11 }}>
            Seçilen uygulama üzerinden mesaj ve bilgileri karşı tarafa iletir.
          </div>
        </div>
      ) : null}

      <div className="driver-table-card">
        <div className="driver-table-card-title">Davetleriniz</div>

        <div className="driver-ref-head">
          <div>Telefon</div>
          <div style={{ textAlign: "right" }}>Yolculuklar</div>
        </div>

        {(rows || []).map((r, idx) => {
          const pill = statusPill(mapStatusLabel(r.status));
          const invitedAt = r.created_at ? new Date(r.created_at).toLocaleString("tr-TR") : (r.invitedAt || "-");
          const bonusText =
            typeof r.reward_amount === "number" ? `${r.reward_amount}₺` : (r.bonus || "0₺");
          const trips = r.orders_count ?? r.orders ?? 0;
          return (
            <div
              key={`${r.id || r.invitee_phone || idx}`}
              className="driver-ref-row"
            >
              <div className="driver-ref-phone-line">
                <div style={{ color: "#111827", fontWeight: 700, minWidth: 0 }}>
                  {r.invitee_phone || r.phone || "-"}
                </div>
                <div style={{ textAlign: "right", color: "#374151", fontWeight: 800, whiteSpace: "nowrap" }}>
                  {trips}
                </div>
              </div>
              <div className="driver-ref-sub">
                <span style={pill.style}>• {pill.label}</span>
                <span style={{ color: "#6b7280", fontWeight: 600, fontSize: 12 }}>{invitedAt}</span>
                <span style={{ color: "#374151", fontWeight: 700, marginLeft: "auto" }}>Bonus: {bonusText}</span>
              </div>
              {r.archived_30d ? (
                <div style={{ marginTop: 4, color: "#6b7280", fontSize: 12, fontWeight: 700 }}>
                  Arşiv (30+ gün inaktif): yeni kayıt gibi bonus uygulanır
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="driver-ref-footnote">
          50 yolculuğa kadar bonus 0₺ görünür. 50 yolculukta 500₺, 100 yolculukta toplam 1000₺ hak edilir.
        </div>
      </div>
    </div>
  );
}

function inviteBtn(bg, color) {
  return {
    height: 34,
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: bg,
    color,
    padding: "0 12px",
    display: "inline-flex",
    alignItems: "center",
    textDecoration: "none",
    fontWeight: 800,
    fontSize: 12.5,
    cursor: "pointer",
  };
}
